import LeaseApiService from '../../services/apiService';
import { endPoint } from '../../EndPoints/endPoint';

// A few thousand rows are imported and compared server-side in one call, so this
// request gets a much longer timeout than the rest of the module.
const IMPORT_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * @typedef {Object} BankFormat
 * @property {string} value        "BBK" | "Khaleeji" | "BBKIntl" - what every other call expects
 * @property {string} text
 * @property {string} sheetName    worksheet the API reads (shown as a hint beside the file picker)
 * @property {string} stagingTable diagnostic only, never displayed
 */

/**
 * One row of the comparison. Which fields are filled depends on the bank format.
 * The mismatch flags are computed by the server and must never be re-derived in the browser.
 * @typedef {Object} ValidationRow
 * @property {number} rowNo
 * @property {string} bankFormat
 * @property {string} refNo
 * @property {string} property
 * @property {string} unitNo
 * @property {string} systemCreditAccount
 * @property {string} systemBeneficiaryName
 * @property {number|null} systemAmount
 * @property {string} bankCreditAccount
 * @property {string} bankBeneficiaryName
 * @property {number|null} bankAmount
 * @property {string} bankSwiftCode     BBKIntl only
 * @property {string} bankProperty      BBKIntl only
 * @property {string} matchStatus       BBKIntl only
 * @property {boolean} isUnitNotFound
 * @property {boolean} isCreditAccountMismatch
 * @property {boolean} isBeneficiaryNameMismatch
 * @property {boolean} isAmountMismatch
 * @property {boolean} hasMismatch
 */

/**
 * @typedef {Object} ValidationTotals
 * @property {number} totalSystemAmount
 * @property {number} totalBankAmount
 * @property {number} difference
 * @property {number} rowCountTotal
 * @property {number} mismatchCount
 * @property {number} unmatchedCount
 * @property {number} nonNumericBankAmounts
 */

/**
 * @typedef {Object} ValidationResult
 * @property {string} bankFormat
 * @property {string} validateBy   full name for the report header
 * @property {string} validatedOn  ISO timestamp
 * @property {ValidationRow[]} rows
 * @property {ValidationTotals} totals
 */

/**
 * @typedef {Object} ImportResult
 * @property {string} bankFormat
 * @property {string} fileName
 * @property {string} sheetName
 * @property {number} rowsImported
 * @property {string[]} unmappedColumns  headings in the sheet with no matching column
 * @property {string[]} missingColumns   columns no heading matched; they stay empty
 * @property {ValidationResult} validation
 */

class BeneficiaryValidationApi {
  constructor() {
    this.apiService = new LeaseApiService();
  }

  getBankFormats() {
    return this.apiService.get('BENEFICIARY_FORMATS');
  }

  // Uses the shared axios instance directly (LeaseApiService.post takes no per-call config)
  // so only this request carries the long timeout. The multipart content type lets the
  // browser add the boundary.
  importBeneficiaryFile(bankFormat, file) {
    const formData = new FormData();
    formData.append('bankFormat', bankFormat);
    formData.append('file', file, file.name);
    return this.apiService.axiosInstance.post(endPoint.BENEFICIARY_IMPORT, formData, {
      timeout: IMPORT_TIMEOUT_MS,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  getValidation(bankFormat) {
    const params = new URLSearchParams();
    params.append('bankFormat', bankFormat || '');
    return this.apiService.getApiParamater('BENEFICIARY_VALIDATION', `?${params.toString()}`);
  }

  clearStaging() {
    return this.apiService.post('BENEFICIARY_CLEAR', {});
  }
}

export default BeneficiaryValidationApi;
